import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useCreateToken, useRevokeToken, useTokens } from "@/api/testing";
import type { ApiToken } from "@/api/types/ApiToken";
import type { CreatedToken } from "@/api/types/CreatedToken";
import { ConfirmDialog, Dialog } from "@/components/Dialog";
import { useToast } from "@/components/Toast";
import { Button, Field, Input, LoadError, Notice, Select, Spinner } from "@/components/ui";
import { relativeTime } from "@/lib/format";
import { useCopy } from "@/lib/useCopy";
import { CliSetupGuide } from "./CliSetupGuide";

const EXPIRY = ["30", "90", "365", "never"] as const;

export const DOCS_URL = "/docs/";
export const DOCS_QUICK_START = "/docs/guide/quick-start.html";

/** Personal access tokens for the mixla CLI. */
export function CliTokensSection() {
  const { t } = useTranslation();
  const tokens = useTokens();
  const focusTokenForm = () => {
    const input = document.querySelector<HTMLInputElement>("#token-form input");
    input?.scrollIntoView({ behavior: "smooth", block: "center" });
    input?.focus({ preventScroll: true });
  };

  return (
    <section className="card flex flex-col gap-4 p-5">
      <h2 className="m-0 label-caps">{t("tokens.title")}</h2>
      <p className="m-0 text-sm text-text-2">
        {t("tokens.intro")}{" "}
        <a href={DOCS_URL} target="_blank" rel="noreferrer">
          {t("tokens.apiDocs")}
        </a>
      </p>
      {tokens.isSuccess && <CliSetupGuide defaultOpen={tokens.data.length === 0} onCreateToken={focusTokenForm} />}
      <TokenManager />
    </section>
  );
}

/**
 * The token list, the create form and the one-time secret dialog. Your own tokens, or with `botId` a bot's
 * (platform managers only).
 */
export function TokenManager({ botId }: { botId?: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const tokens = useTokens(true, botId);
  const create = useCreateToken(botId);
  const [name, setName] = useState("");
  const [expiry, setExpiry] = useState<(typeof EXPIRY)[number]>("90");
  const [created, setCreated] = useState<CreatedToken | null>(null);
  const [revoking, setRevoking] = useState<ApiToken | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate(
      { name: name.trim(), expires_in_days: expiry === "never" ? null : Number(expiry) },
      {
        onSuccess: (res) => {
          setCreated(res);
          setName("");
        },
        onError: (err) => toast.error(err),
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {tokens.isPending ? (
        <Spinner />
      ) : tokens.isError ? (
        <LoadError onRetry={() => void tokens.refetch()} />
      ) : tokens.data.length === 0 ? (
        <p className="m-0 text-sm text-muted">{t("tokens.empty")}</p>
      ) : (
        <div className="relative overflow-x-auto rounded-[var(--radius-ctl)] border border-border">
          <table className="table-base min-w-[560px]">
            <thead>
              <tr>
                <th>{t("tokens.name")}</th>
                <th>{t("tokens.created")}</th>
                <th>{t("tokens.lastUsed")}</th>
                <th>{t("tokens.expires")}</th>
                <th className="w-px">
                  <span className="sr-only">{t("common.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {tokens.data.map((tok) => (
                <tr key={tok.id}>
                  <td>
                    <span className="font-medium">{tok.name}</span>
                    <span className="ml-2 font-mono text-xs text-muted">{tok.prefix}…</span>
                  </td>
                  <td className="text-muted">{relativeTime(tok.created_at)}</td>
                  <td className="text-muted">{tok.last_used_at ? relativeTime(tok.last_used_at) : t("common.never")}</td>
                  <td className="text-muted">{tok.expires_at ? relativeTime(tok.expires_at) : t("common.never")}</td>
                  <td>
                    <Button size="sm" variant="danger" onClick={() => setRevoking(tok)}>
                      {t("tokens.revoke")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form id={botId ? "bot-token-form" : "token-form"} onSubmit={submit} className="flex flex-wrap items-end gap-3" noValidate>
        <div className="min-w-[220px] flex-1">
          <Field label={t("tokens.name")}>
            {(p) => (
              <Input {...p} value={name} onChange={(e) => setName(e.target.value)} placeholder={t("tokens.namePlaceholder")} maxLength={80} />
            )}
          </Field>
        </div>
        <div className="w-40">
          <Field label={t("tokens.expiry")}>
            {(p) => (
              <Select {...p} value={expiry} onChange={(e) => setExpiry(e.target.value as (typeof EXPIRY)[number])}>
                {EXPIRY.map((x) => (
                  <option key={x} value={x}>
                    {x === "never" ? t("common.never") : t("tokens.days", { count: Number(x) })}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <Button type="submit" variant="primary" icon="key" loading={create.isPending} disabled={!name.trim()}>
          {t("tokens.create")}
        </Button>
      </form>

      <CreatedTokenDialog created={created} bot={!!botId} onClose={() => setCreated(null)} />
      <RevokeDialog token={revoking} botId={botId} onClose={() => setRevoking(null)} />
    </div>
  );
}

function CreatedTokenDialog({ created, bot, onClose }: { created: CreatedToken | null; bot: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const secret = useCopy();
  const command = useCopy();
  const cmd = !created
    ? ""
    : bot
      ? `curl -H "Authorization: Bearer ${created.secret}" ${window.location.origin}/api/v1/auth/whoami`
      : `mixla login --url ${window.location.origin} --token ${created.secret}`;
  return (
    <Dialog
      open={!!created}
      onClose={onClose}
      dismissable={false}
      width={620}
      title={t("tokens.createdTitle")}
      footer={
        <Button variant="primary" onClick={onClose}>
          {t("tokens.done")}
        </Button>
      }
    >
      {created && (
        <>
          <Notice tone="blocked">{t("tokens.createdWarning")}</Notice>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 rounded-md bg-surface-2 px-2.5 py-2 font-mono text-sm break-all">{created.secret}</code>
            <Button size="sm" icon="copy" onClick={() => void secret.copy(created.secret)}>
              {secret.copied ? t("common.copied") : t("common.copy")}
            </Button>
          </div>
          <span className="text-sm font-medium text-text-2">{bot ? t("tokens.tryIt") : t("tokens.command")}</span>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 rounded-md bg-surface-2 px-2.5 py-2 font-mono text-xs break-all">{cmd}</code>
            <Button size="sm" icon="terminal" onClick={() => void command.copy(cmd)}>
              {command.copied ? t("common.copied") : t("common.copy")}
            </Button>
          </div>
          {bot && (
            <a href={DOCS_QUICK_START} target="_blank" rel="noreferrer" className="text-sm">
              {t("tokens.howToUse")}
            </a>
          )}
        </>
      )}
    </Dialog>
  );
}

function RevokeDialog({ token, botId, onClose }: { token: ApiToken | null; botId?: string; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const revoke = useRevokeToken(botId);
  return (
    <ConfirmDialog
      open={!!token}
      onClose={onClose}
      danger
      title={t("tokens.revokeTitle", { name: token?.name ?? "" })}
      body={botId ? t("tokens.revokeBotBody") : t("tokens.revokeBody")}
      confirmLabel={t("tokens.revoke")}
      loading={revoke.isPending}
      onConfirm={() =>
        token &&
        revoke.mutate(token.id, {
          onSuccess: () => {
            toast.success(t("tokens.revoked"));
            onClose();
          },
          onError: (e) => toast.error(e),
        })
      }
    />
  );
}
